import { useState, useCallback } from "react";

/**
 * Custom hook for form handling with validation
 * @template T - Type extending Record<string, string> for form data
 * @param {T} initialState - Initial state of the form
 * @param {Record<keyof T, (value: string, formData: T) => string | null>} validationRules - Validation rules for each field
 * @param {() => void} onSubmit - Function to be called on successful form submission
 * @returns {Object} Form handling methods and state
 */
export const useForm = <T extends Record<string, string>>(
  initialState: T,
  validationRules: Record<
    keyof T,
    (value: string, formData: T) => string | null
  >,
  onSubmit: () => void
) => {
  /** State for form data */
  const [formData, setFormData] = useState<T>(initialState);

  /** State for form errors */
  const [errors, setErrors] = useState<Record<keyof T, string | null>>(
    {} as Record<keyof T, string | null>
  );

  /**
   * Validates a single field
   * @param {keyof T} name - Name of the field to validate
   * @param {string} value - Value of the field to validate
   * @returns {string | null} Error message or null if valid
   */
  const validateField = useCallback(
    (name: keyof T, value: string) => {
      const error = validationRules[name](value, formData);
      setErrors((prev) => ({ ...prev, [name]: error }));
      return error;
    },
    [formData, validationRules]
  );

  /**
   * Handles changes in form inputs
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} e - Change event
   */
  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      validateField(name as keyof T, value);
    },
    [validateField]
  );

  /**
   * Handles form submission
   * @param {React.FormEvent} e - Form submission event
   */
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const newErrors = Object.keys(formData).reduce((acc, key) => {
        const error = validateField(key as keyof T, formData[key as keyof T]);
        if (error) acc[key as keyof T] = error;
        return acc;
      }, {} as Record<keyof T, string | null>);

      setErrors(newErrors);

      if (Object.values(newErrors).every((error) => !error)) {
        onSubmit();
      }
    },
    [formData, validateField, onSubmit]
  );

  return { formData, errors, handleChange, handleSubmit, validateField };
};
